import { useRef, useEffect, useCallback } from 'react';
import {
  GridApiCommon,
  GridInitialState,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';

/**
 * Hook to manage column state persistence (widths, order) across rerenders.
 * Automatically resets column widths when columns are added/removed.
 */
export const useColumnState = (
  apiRef: React.MutableRefObject<GridApiCommon>,
  columnVisibilityModel: GridColumnVisibilityModel
) => {
  const gridStateRef = useRef<GridInitialState>({});
  const previousVisibilityModel = useRef<GridColumnVisibilityModel>(
    columnVisibilityModel
  );
  const isInitialRender = useRef(true);

  // Subscribe to column changes and capture state
  const handleColumnChange = useCallback(() => {
    if (!apiRef.current) {
      return;
    }

    const currentState = apiRef.current.exportState();
    // Store in ref to avoid triggering re-renders
    gridStateRef.current = currentState;
  }, [apiRef]);

  // Reset column sizes when visibility model changes (columns added/removed)
  useEffect(() => {
    const visibilityChanged =
      JSON.stringify(previousVisibilityModel.current) !==
      JSON.stringify(columnVisibilityModel);

    if (visibilityChanged && gridStateRef.current.columns) {
      delete gridStateRef.current.columns.dimensions;
      previousVisibilityModel.current = columnVisibilityModel;
    }
  }, [columnVisibilityModel]);

  // Restore column state on rerender
  useEffect(() => {
    // Skip initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (!apiRef.current || !gridStateRef.current.columns) {
      return;
    }

    // Use setTimeout to ensure restoration happens after DataGrid has settled
    // This is needed for the empty state to render with the correct widths
    const timeoutId = setTimeout(() => {
      if (apiRef.current && gridStateRef.current.columns) {
        apiRef.current.restoreState({
          columns: {
            ...gridStateRef.current.columns,
            columnVisibilityModel: columnVisibilityModel,
          },
        });
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  });

  return { handleColumnChange };
};
